import { z } from 'zod';
import { dealFactsSchema } from './deal-facts.js';
const paise = z.number().int().min(0).max(Number.MAX_SAFE_INTEGER);
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
export const createDealSchema = z
    .object({
    visitId: z.string().uuid().optional(),
    propertyId: z.string().uuid().optional(),
    manualClient: z
        .object({
        name: z.string().trim().min(2).max(80),
        phone: z.string().trim().min(1).max(20),
        /** The broker attests the client agreed to share their number (R10). */
        consentAttested: z.literal(true),
    })
        .optional(),
    ownerPartyName: z.string().trim().min(1).max(80).optional(),
    ownerPartyPhone: z.string().trim().min(1).max(20).optional(),
})
    .refine((v) => v.visitId !== undefined || (v.propertyId !== undefined && v.manualClient !== undefined), {
    message: 'Start from a visit, or give a property and a client',
});
export const dealTermsSchema = z
    .object({
    token: z
        .object({
        amountPaise: paise.refine((n) => n > 0, 'Token must be above zero'),
        paidTo: z.string().trim().min(1).max(80),
        mode: z.enum(['upi', 'cash', 'bank', 'cheque']),
        date: isoDate,
    })
        .optional(),
    brokerage: z
        .object({
        totalPaise: paise,
        split: z.object({ tenantPaise: paise, ownerPaise: paise }),
        dueOn: isoDate.nullable().optional(),
        incentive: z
            .union([
            z.object({ type: z.literal('percent'), bps: z.number().int().min(0).max(10_000) }),
            z.object({ type: z.literal('fixed'), paise }),
        ])
            .nullable()
            .optional(),
    })
        .optional(),
    ownerPartyName: z.string().trim().min(1).max(80).optional(),
    ownerPartyPhone: z.string().trim().min(1).max(20).optional(),
    notes: z.string().trim().max(2000).nullable().optional(),
    /** Each key given replaces that part of the deal's facts; the others stay. */
    facts: dealFactsSchema.partial().optional(),
})
    .refine((v) => Object.keys(v).length > 0, 'Provide at least one field');
export const dealTransitionSchema = z.object({
    to: z.enum([
        'token_recorded',
        'memo_signed',
        'agreement_drafted',
        'registered',
        'closed',
        'lost',
        'cancelled',
    ]),
    ownerOverrideReason: z.string().trim().min(3).max(300).optional(),
});
export const receiptSchema = z.object({
    amountPaise: paise.refine((n) => n > 0, 'Amount must be above zero'),
    receivedOn: isoDate,
    mode: z.enum(['upi', 'cash', 'bank', 'cheque']),
    reference: z.string().trim().max(80).optional(),
    payer: z.enum(['tenant', 'owner']),
});
export const reassignDealSchema = z.object({ memberId: z.string().uuid() });
export const agreementSchema = z.object({
    termMonths: z.number().int().min(1).max(60).default(11),
    startDate: isoDate,
    rentPaise: paise.refine((n) => n > 0, 'Rent must be above zero'),
    depositPaise: paise,
    locality: z.enum(['urban', 'rural']).default('urban'),
    details: z.object({
        licensor: z.string().trim().min(2).max(120),
        licensee: z.string().trim().min(2).max(120),
        address: z.string().trim().min(5).max(300),
        escalationPercent: z.number().min(0).max(100).default(0),
        lockInMonths: z.number().int().min(0).max(60).default(0),
        noticeDays: z.number().int().min(0).max(365).default(30),
        permittedUse: z.string().trim().min(2).max(120).default('Residential'),
        extraClauses: z.string().trim().max(2000).optional(),
        /** The language of the draft and the clause library keys that are on (checked against the library by the API). */
        lang: z.enum(['en', 'mr', 'hi']).default('en'),
        clauses: z.array(z.string().max(40)).max(20).default([]),
        refundDays: z.number().int().min(1).max(180).default(30),
    }),
});
/** The money fields of the agreement form: enough for a live estimate, nothing is saved. */
export const agreementEstimateSchema = agreementSchema.pick({
    termMonths: true,
    rentPaise: true,
    depositPaise: true,
    locality: true,
});
export const registrationSchema = z.object({
    number: z.string().trim().min(2).max(60),
    date: isoDate,
    sroOffice: z.string().trim().min(2).max(120),
});
export const partnerSchema = z.object({
    name: z.string().trim().min(2).max(80),
    phone: z.string().trim().min(1).max(20),
    area: z.string().trim().max(80).optional(),
});
export const partnerJobSchema = z.object({
    partnerId: z.string().uuid(),
    slot: z.string().trim().max(60).optional(),
    referralPaise: paise.optional(),
});
export const signOtpSchema = z.object({ code: z.string().regex(/^\d{6}$/) });
export const partnerUpdateSchema = z.object({
    status: z.enum(['visit_booked', 'completed']),
    slot: z.string().trim().max(60).optional(),
    registrationNumber: z.string().trim().min(2).max(60).optional(),
    registrationDate: isoDate.optional(),
    sroOffice: z.string().trim().min(2).max(120).optional(),
});
export const clientPhoneSchema = z.object({ phone: z.string().trim().min(1).max(20) });
/** Which side to remind about the brokerage they owe. */
export const brokerageNudgeSchema = z.object({ party: z.enum(['tenant', 'owner']) });
//# sourceMappingURL=deal.js.map